<template>
    <div v-if="notice" class="billing-banner" :class="`billing-banner--${notice.tone}`" role="status" data-tour="billing-notice">
        <span class="billing-banner__text">{{ notice.message }}</span>
        <button v-if="notice.action && canManage" type="button" class="billing-banner__action" @click="openBilling">
            {{ notice.action }}
        </button>
        <span v-else-if="notice.action" class="billing-banner__aside">Ask the owner.</span>
    </div>
</template>

<script setup>
import { useConfigurationStore } from '@/stores/configuration-store.js';
import { todayLocal } from '@/utils/businessDate.js';
import { computed, onUnmounted, watch } from 'vue';
import { useRouter } from 'vue-router';

/**
 * The subscription bar.
 *
 * One line, always visible, never dismissable, on every shell: the one thing
 * every user of a lapsed installation must see whether or not they are the one
 * who can fix it. The state comes from the configuration store — `/billing/state`
 * on boot, every 402 after — and the sentence is composed here from the dates
 * and the amount, so "5 days left" moves at midnight without a request.
 *
 * Nothing renders for `none`, `free` or `active`, and a trial says nothing
 * until its last week: a bar that is always there is a bar nobody reads.
 *
 * The bar sits in flow at the top of the document and toggles `billing-notice`
 * on <html> while visible; app.css turns that class into --app-banner-offset,
 * which the sticky topbar and breadcrumbs add to their own offsets. A class
 * rather than a bare `html {}` rule from this file, because an unscoped SFC
 * style outlives the component and leaks across routes.
 */
const TRIAL_NOTICE_DAYS = 7;
const DAY_MS = 86_400_000;
const HTML_CLASS = 'billing-notice';

const configStore = useConfigurationStore();
const router = useRouter();

const state = computed(() => configStore.billingState);
const canManage = computed(() => configStore.billingCanManage);

const money = (value) => Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** `8 Oct 2026` from `2026-10-08`, read as a local calendar day. */
const dayLabel = (value) => {
    if (!value) return '';
    const [y, m, d] = String(value)
        .slice(0, 10)
        .split('-')
        .map((part) => Number(part));
    if (!y || !m || !d) return String(value);
    return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

/** Whole days from today (local) to a `YYYY-MM-DD`, never negative. */
const daysUntil = (value) => {
    if (!value) return null;
    const toDate = (s) => {
        const [y, m, d] = String(s)
            .slice(0, 10)
            .split('-')
            .map((part) => Number(part));
        return new Date(y, m - 1, d);
    };
    const diff = Math.round((toDate(value) - toDate(todayLocal())) / DAY_MS);
    return Number.isFinite(diff) ? Math.max(0, diff) : null;
};

const daysLeft = (n) => (n === 1 ? '1 day left' : `${n} days left`);

const notice = computed(() => {
    const s = state.value;
    if (!s || !s.armed) return null;

    const invoice = s.invoice ?? null;
    const due = invoice ? money(invoice.amount_due ?? invoice.total) : null;
    const period = invoice?.period_label ? ` for ${invoice.period_label}` : '';
    const tail = s.notice ? ` ${s.notice}` : '';

    switch (s.state) {
        case 'trial': {
            const left = daysUntil(s.trial_ends_on);
            if (left === null || left > TRIAL_NOTICE_DAYS) return null;
            return {
                tone: 'info',
                message: `Trial ends on ${dayLabel(s.trial_ends_on)}. Add a payment method to continue without interruption.`,
                action: 'Add payment method',
            };
        }
        case 'setup_required': {
            const left = daysUntil(s.setup_due_on);
            const when = s.setup_due_on ? ` by ${dayLabel(s.setup_due_on)}` : '';
            const remaining = left === null ? '' : ` (${daysLeft(left)})`;
            return {
                tone: 'warning',
                message: `Add a payment method${when} to keep using Inventree${remaining}.${tail}`,
                action: 'Add payment method',
            };
        }
        case 'past_due':
            return {
                tone: 'warning',
                message: invoice
                    ? `Subscription payment of Rs ${due}${period} was due on ${dayLabel(invoice.due_on)}. Pay by ${dayLabel(invoice.grace_until)} to avoid interruption.${tail}`
                    : `A subscription payment is overdue. Pay it to avoid interruption.${tail}`,
                action: 'Pay now',
            };
        case 'locked': {
            const consequence =
                s.lock_mode === 'full'
                    ? 'Access is suspended until it is paid.'
                    : s.lock_mode === 'none'
                      ? ''
                      : 'Inventree is read-only until it is paid.';
            if (!invoice) {
                const setupConsequence =
                    s.lock_mode === 'full'
                        ? 'Access is suspended until one is added.'
                        : s.lock_mode === 'none'
                          ? ''
                          : 'Inventree is read-only until one is added.';
                return {
                    tone: 'danger',
                    message: `Add a payment method to continue. ${setupConsequence}${tail}`.trim(),
                    action: 'Add payment method',
                };
            }
            return {
                tone: 'danger',
                message: `Subscription payment of Rs ${due}${period} is overdue. ${consequence}${tail}`.trim(),
                action: 'Pay now',
            };
        }
        case 'suspended':
            return {
                tone: 'danger',
                message: s.notice || s.message || 'Your subscription has been suspended. Contact the vendor.',
                action: null,
            };
        case 'ended':
            return {
                tone: 'danger',
                message: `Your subscription has ended.${tail}`,
                action: null,
            };
        default:
            return null;
    }
});

const openBilling = () => {
    router.push({ name: 'billing' }).catch(() => {});
};

const setHtmlClass = (on) => {
    if (typeof document === 'undefined') return;
    document.documentElement.classList.toggle(HTML_CLASS, Boolean(on));
};

watch(() => Boolean(notice.value), setHtmlClass, { immediate: true });

onUnmounted(() => setHtmlClass(false));
</script>

<style>
.billing-banner {
    position: sticky;
    top: 0;
    z-index: 50;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    flex-wrap: wrap;
    min-height: 2.5rem;
    padding: 6px 12px;
    color: white;
    font-size: 13px;
    line-height: 1.3;
    text-align: center;
}

.billing-banner--info {
    background: #1d4ed8;
}

.billing-banner--warning {
    background: #b45309;
}

.billing-banner--danger {
    background: #b91c1c;
}

.billing-banner__aside {
    opacity: 0.85;
}

.billing-banner__action {
    padding: 2px 10px;
    border: 1px solid rgba(255, 255, 255, 0.6);
    border-radius: 4px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
}

.billing-banner__action:hover {
    background: rgba(255, 255, 255, 0.15);
}
</style>
